import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Grid,
  Alert,
} from '@mui/material';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getDocumentUrl = (path) => {
  if (!path) return '';
  const url = path.startsWith('http') ? path : `${API_URL}/${path}`;
  const ext = path.split('.').pop().toLowerCase().split('?')[0];
  
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
    return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`;
  }
  return url;
};

const ManagerApproval = () => {
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [managerId, setManagerId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');




  const getEmployeeid = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        withCredentials: true
      });

      if (!response.data || !response.data.employee_id) {
        alert('Session Expired, Please login again')
        throw new Error('Employee ID not found in user data');
      }

      setEmployeeId(response.data.employee_id);
      
      return response.data.employee_id;
    } catch (err) {
      //console.error("Error fetching current user:", err);
      setError('Failed to fetch user information');
      return null;
    }
  };
  // useEffect(() => {
  //   setManagerId('current_manager_id');
  //   fetchAllPendingLeaves('current_manager_id');
  // }, []);

  useEffect(() => {
    const init = async () => {
      const manager_id = await getEmployeeid();

      if (!manager_id) return;

      setManagerId(manager_id);
      fetchAllPendingLeaves(manager_id);
    };

    init();
  }, []);


  const fetchAllPendingLeaves = async (manager_id) => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError("No authentication token found. Please log in.");
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;


      const response = await fetch(`${API_URL}/api/leaves/pending/${manager_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        withCredentials: true
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch pending leaves');
      }

      const data = await response.json();

      setPendingLeaves(data);

      if (data.length === 0) {
        setSuccess('No pending leave requests at this time.');
      }
    } catch (error) {
      //console.error('[FETCH] Error:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (status) => {
    if (!selectedLeave) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError("No authentication token found. Please log in.");
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;


      const requestBody = {
        status,
        manager_comment: comment || '',
        company_id // CRITICAL: Include company_id in body
      };


      const response = await fetch(`${API_URL}/api/leaves/update/${selectedLeave.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${status.toLowerCase()} leave`);
      }

      const result = await response.json();

      // Remove the leave from the pending list
      setPendingLeaves(pendingLeaves.filter(leave => leave.id !== selectedLeave.id));

      // Close dialog and reset
      setSelectedLeave(null);
      setComment('');

      setSuccess(`Leave ${status.toLowerCase()} successfully. Email notification has been sent to the employee.`);

      // Auto-clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);

    } catch (error) {
      //console.error('[DECISION] Error:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="1500px" sx={{ py: 4 }}>
      <Paper sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        border: '2px solid',
        borderColor: 'grey.300',
        boxShadow: 4
      }}>
        <Typography variant="h4" gutterBottom  fontWeight="bold">
        Leave Requests
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
      </Paper>

      {!loading && pendingLeaves.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">No pending leave requests found</Typography>
        </Paper>
      ) : (
        pendingLeaves.map(leave => (
          <Paper key={leave.id} sx={{ p: 3, my: 2, borderLeft: '2px solid', borderColor: 'warning.main' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={2}>
                <Typography variant="subtitle2" color="text.secondary">Employee ID:</Typography>
                <Typography variant="body1" fontWeight="medium">{leave.employeeId || leave.employee_id}</Typography>
              </Grid>
              <Grid item xs={12} sm={5}>
                <Typography variant="subtitle2" color="text.secondary">Employee Email:</Typography>
                <Typography variant="body1" fontWeight="medium">{leave.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Typography variant="subtitle2" color="text.secondary">Leave Type:</Typography>
                <Typography variant="body1" fontWeight="medium" textTransform="capitalize">
                  {leave.leaveType}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="subtitle2" color="text.secondary">Duration:</Typography>
                <Typography variant="body1">
                  {new Date(leave.startDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {' to '}
                  {new Date(leave.endDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {leave.lastDay && ' (Last day: Half day)'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Reason:</Typography>
                <Typography variant="body2">{leave.reason}</Typography>
              </Grid>
              {leave.document_url && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    size="small"
                    href={getDocumentUrl(leave.document_url)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    📎 View Attached Document
                  </Button>
                </Grid>
              )}
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Chip
                  label={leave.status}
                  color="warning"
                  sx={{ fontWeight: 'medium' }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setSelectedLeave(leave)}
                  sx={{ ml: 'auto' }}
                >
                  Review
                </Button>
              </Grid>
            </Grid>
          </Paper>
        ))
      )}

      <Dialog open={!!selectedLeave} onClose={() => setSelectedLeave(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" textTransform="capitalize">
            {selectedLeave?.leaveType} Leave Request - {selectedLeave?.employeeId || selectedLeave?.employee_id}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Employee Email:</Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedLeave?.email}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Duration:</Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedLeave && new Date(selectedLeave.startDate).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                {' to '}
                {selectedLeave && new Date(selectedLeave.endDate).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                {selectedLeave?.lastDay && ' (Last day: Half day)'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Reason:</Typography>
              <Typography variant="body1">{selectedLeave?.reason}</Typography>
            </Grid>
            {selectedLeave?.document_url && (
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  size="small"
                  href={getDocumentUrl(selectedLeave.document_url)}
                  target="_blank"
                  rel="noreferrer"
                >
                  📎 View Attached Document
                </Button>
              </Grid>
            )}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <TextField
                label="Manager Comment (Optional)"
                multiline
                rows={3}
                fullWidth
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add any comments about this leave request..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setSelectedLeave(null);
              setComment('');
            }}
            color="inherit"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleDecision('Rejected')}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            Reject
          </Button>
          <Button
            color="success"
            variant="contained"
            onClick={() => handleDecision('Approved')}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManagerApproval;